import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Plus, Menu, Tv2, Trash2, FileText, ShieldCheck, LogOut, AlertTriangle } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/lib/AuthContext';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { useOutboxStatus } from '@/hooks/useOutboxStatus';
import PageContainer from '@/components/PageContainer';

/**
 * The bottom nav, task: bottom nav on every screen (2026-09-08). Mounted
 * once in App.jsx's AppShellLayout, around every authenticated route, not
 * per page: identical contents everywhere, on purpose, the owner's own
 * reasoning being that a nav whose meaning changes per screen is worse than
 * one that never grows a fourth button. Not shown on login, forgot
 * password or reset password, since those routes sit outside
 * AppShellLayout entirely, nothing to configure per page for that.
 *
 * Home highlights active when already on Home, and every navigating
 * control here (Home, +, and everything inside More, including sign out)
 * goes through guardedAction rather than a plain Link, so the unsaved
 * changes guard (useNavigationGuard.jsx) can intercept it from any screen,
 * not just the page's own back arrow.
 *
 * Still deliberately just three top level items (Home, +, More): Profile
 * has its own permanent icon in the header instead of a nav slot, since
 * every rep needs it and an icon that only appears for admins would make
 * the header inconsistent between reps.
 *
 * The center + only renders on Home (2026-09-08). Home and More stay on
 * every screen, but + is a Home-specific shortcut into capture; showing it
 * on the capture form itself, or on Shows/Templates/Admin/Profile/Deleted
 * Contacts/contact detail, was noise rather than a real shortcut there.
 */
export default function BottomNav() {
  const { isAdmin, logout } = useAuth();
  const { guardedAction } = useNavigationGuard();
  const { stuck: stuckSyncCount } = useOutboxStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const isHomeActive = location.pathname === '/';

  const items = [
    { to: '/shows', label: 'Shows', icon: Tv2 },
    { to: '/deleted-contacts', label: 'Deleted Contacts', icon: Trash2 },
    { to: '/templates', label: 'Templates', icon: FileText },
    {
      to: '/stuck-captures',
      label: stuckSyncCount > 0 ? `Stuck Captures (${stuckSyncCount})` : 'Stuck Captures',
      icon: AlertTriangle,
    },
    // Never shown in the iOS build, task: Capacitor scaffolding (2026-09-08),
    // regardless of who is signed in: admin is web only (CLAUDE.md section
    // 2), and App.jsx's WebOnlyRoute already refuses the route itself, so
    // this is just keeping the entry point consistent with that, not the
    // only thing enforcing it.
    ...(isAdmin && !Capacitor.isNativePlatform() ? [{ to: '/admin', label: 'Admin', icon: ShieldCheck }] : []),
  ];

  const goTo = (path) => guardedAction(() => navigate(path));

  return (
    <>
      <div className="fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border safe-bottom">
        {/* PageContainer itself now goes full width at lg: and up (task:
            desktop layout, no max-width cap). A nav bar spreading its own
            three buttons across that full width with justify-between would
            leave Home and More pinned to the far edges of the browser with
            a huge empty gap between them, which is not what a bottom nav
            should do on a wide screen even though it is exactly what a
            page's own content should do. So PageContainer here only centers
            its child; the actual button row lives in its own max-w-lg
            group underneath, unaffected by how wide PageContainer itself
            gets. Below lg, PageContainer is still max-w-lg exactly as
            before, so this inner group always fills it completely and
            nothing changes on mobile or iOS. */}
        <PageContainer className="px-6 h-16 flex items-center justify-center">
          <div className="w-full max-w-lg flex items-center justify-between">
            {/* Home tab, task: 2026-09-25. Removed entirely on Home itself
                (a spacer keeps + and More from shifting position, since the
                Home page is the one place tapping it would serve no
                purpose), and always the same accent orange on every other
                page rather than switching between an active/inactive color:
                every other page is somewhere that is NOT home, so it always
                reads as the same clear "return home" call to action there,
                never a state indicator. */}
            {isHomeActive ? (
              <div className="w-14" aria-hidden="true" />
            ) : (
              <button
                type="button"
                onClick={() => goTo('/')}
                className="flex flex-col items-center justify-center gap-0.5 w-14 text-accent"
              >
                <Home className="w-5 h-5" />
                <span className="text-[10px] font-medium">Home</span>
              </button>
            )}

            {isHomeActive && (
              <button
                type="button"
                onClick={() => goTo('/new')}
                className="w-16 h-16 -mt-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow-lg shadow-accent/30 border-4 border-background"
                title="New contact"
              >
                <Plus className="w-8 h-8" />
              </button>
            )}

            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className="flex flex-col items-center justify-center gap-0.5 text-muted-foreground w-14"
            >
              <Menu className="w-5 h-5" />
              <span className="text-[10px] font-medium">More</span>
            </button>
          </div>
        </PageContainer>
      </div>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl max-w-lg mx-auto">
          <SheetHeader>
            <SheetTitle>More</SheetTitle>
          </SheetHeader>
          <div className="space-y-1 mt-3">
            {items.map(({ to, label, icon: Icon }) => (
              <button
                key={to}
                type="button"
                onClick={() => { setMoreOpen(false); goTo(to); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-secondary/50 text-foreground font-medium text-sm"
              >
                <Icon className="w-4 h-4 text-muted-foreground" />
                {label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setMoreOpen(false); guardedAction(() => logout()); }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-secondary/50 text-destructive font-medium text-sm"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
